import axios from 'axios';
import { getServerUrl } from '../config';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { db } from '../firebase';

class GoogleDriveService {
  /**
   * Initialize Google Drive API authentication
   * @param {string} userId - Firebase user ID
   * @returns {Promise<Object>} - Authentication status
   */
  static async initializeAuth(userId) {
    try {
      // Get the auth URL from our server
      const response = await axios.get(`${getServerUrl('proxy')}/api/google-drive/auth-url`, {
        params: { userId }
      });
      
      return {
        success: true,
        authUrl: response.data.authUrl
      };
    } catch (error) {
      console.error('Error initializing Google Drive auth:', error);
      return {
        success: false,
        error: error.message || 'Failed to initialize Google Drive authentication'
      };
    }
  }

  /**
   * Check if user has valid Google Drive credentials
   * @param {string} userId - Firebase user ID
   * @returns {Promise<boolean>} - Whether user has valid credentials
   */
  static async hasValidCredentials(userId) {
    try {
      const userDoc = await getDoc(doc(db, 'users', userId));
      if (!userDoc.exists()) {
        return false;
      }
      
      const userData = userDoc.data();
      return !!(userData.googleDrive && userData.googleDrive.tokens && userData.googleDrive.tokens.access_token);
    } catch (error) {
      console.error('Error checking Google Drive credentials:', error);
      return false;
    }
  }

  /**
   * Get Google Drive settings for a user
   * @param {string} userId - Firebase user ID
   * @returns {Promise<Object>} - Google Drive settings
   */
  static async getSettings(userId) {
    try {
      const userDoc = await getDoc(doc(db, 'users', userId));
      if (!userDoc.exists()) {
        return { enabled: false };
      }
      
      const userData = userDoc.data();
      return {
        enabled: !!(userData.googleDrive && userData.googleDrive.enabled),
        folderNamingOption: userData.googleDrive?.folderNamingOption || 'productName',
        mainFolder: userData.googleDrive?.mainFolder || 'Editify Product Images',
        hasValidCredentials: !!(userData.googleDrive && userData.googleDrive.tokens && userData.googleDrive.tokens.access_token)
      };
    } catch (error) {
      console.error('Error getting Google Drive settings:', error);
      return { enabled: false };
    }
  }

  /**
   * Save Google Drive settings for a user
   * @param {string} userId - Firebase user ID
   * @param {Object} settings - Google Drive settings
   * @returns {Promise<boolean>} - Success status
   */
  static async saveSettings(userId, settings) {
    try {
      const userRef = doc(db, 'users', userId);
      const userDoc = await getDoc(userRef);
      
      if (!userDoc.exists()) {
        throw new Error('User document not found');
      }
      
      // Update only the provided settings
      const updateData = {
        'googleDrive.enabled': settings.enabled !== undefined ? settings.enabled : false,
        'googleDrive.folderNamingOption': settings.folderNamingOption || 'productName'
      };
      
      // Only update mainFolder if it's provided
      if (settings.mainFolder) {
        updateData['googleDrive.mainFolder'] = settings.mainFolder;
      }
      
      await updateDoc(userRef, updateData);
      
      return true;
    } catch (error) {
      console.error('Error saving Google Drive settings:', error);
      return false;
    }
  }

  /**
   * Upload an image to Google Drive
   * @param {string} userId - Firebase user ID
   * @param {string} imageUrl - URL of the image to upload
   * @param {string} folderName - Name of the folder to upload to
   * @param {string} fileName - Name of the file to create
   * @returns {Promise<Object>} - Upload result
   */
  static async uploadImage(userId, imageUrl, folderName, fileName) {
    try {
      console.log(`GoogleDriveService: Starting upload for user ${userId}`);
      console.log(`GoogleDriveService: Image URL: ${imageUrl}`);
      console.log(`GoogleDriveService: Folder: ${folderName}, File: ${fileName}`);
      
      // Get server URL and log it
      const serverUrl = getServerUrl('proxy');
      console.log(`GoogleDriveService: Using server URL: ${serverUrl}`);
      
      // Call our server to handle the upload
      const response = await axios.post(`${serverUrl}/api/google-drive/upload`, {
        userId,
        imageUrl,
        folderName,
        fileName
      });
      
      console.log('GoogleDriveService: Upload successful:', response.data);
      
      return {
        success: true,
        fileId: response.data.fileId,
        webViewLink: response.data.webViewLink
      };
    } catch (error) {
      // Enhanced error logging
      console.error('GoogleDriveService: Error uploading to Google Drive:', error);
      
      // Log more details about the error
      if (error.response) {
        // The request was made and the server responded with a status code
        // that falls out of the range of 2xx
        console.error('GoogleDriveService: Error response data:', error.response.data);
        console.error('GoogleDriveService: Error response status:', error.response.status);
        console.error('GoogleDriveService: Error response headers:', error.response.headers);
      } else if (error.request) {
        // The request was made but no response was received
        console.error('GoogleDriveService: No response received:', error.request);
      } else {
        // Something happened in setting up the request that triggered an Error
        console.error('GoogleDriveService: Error setting up request:', error.message);
      }
      
      return {
        success: false,
        error: (error.response && error.response.data && error.response.data.error) || 
               error.message || 
               'Failed to upload image to Google Drive'
      };
    }
  }

  /**
   * Revoke Google Drive access
   * @param {string} userId - Firebase user ID
   * @returns {Promise<boolean>} - Success status
   */
  static async revokeAccess(userId) {
    try {
      // Get the existing tokens from Firestore
      let accessToken = null;
      try {
        const userDoc = await getDoc(doc(db, 'users', userId));
        if (userDoc.exists()) {
          const userData = userDoc.data();
          accessToken = userData.googleDrive?.tokens?.access_token || null;
        }
      } catch (fetchError) {
        console.warn('Could not fetch existing token:', fetchError);
      }
      
      // Call our server to revoke tokens
      await axios.post(`${getServerUrl('proxy')}/api/google-drive/revoke`, {
        userId,
        token: accessToken // Send the token to be revoked on Google's side
      });
      
      // Update user document to remove tokens
      const userRef = doc(db, 'users', userId);
      await updateDoc(userRef, {
        'googleDrive.tokens': null,
        'googleDrive.enabled': false
      });
      
      return true;
    } catch (error) {
      console.error('Error revoking Google Drive access:', error);
      return false;
    }
  }
}

export default GoogleDriveService;
